"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GlobalStyle = exports.BaseStyles = void 0;
const styled_components_1 = require("styled-components");
const tokens_1 = require("../../styles/tokens");
/**
 * Base styles has to be included in the root of the application.
 */
exports.BaseStyles = (0, styled_components_1.createGlobalStyle) `
    :root {
        /* Greyscale colors */
        --ds-color-night: #191c4d;
        --ds-color-zinc: #363963;
        --ds-color-metal: #44487e;
        --ds-color-stone: #8184a7;
        --ds-color-silver: #e0e1eb;
        --ds-color-dishwater: #eaeaf0;
        --ds-color-cloud: #f1f1f4;
        --ds-color-white: #ffffff;

        /* Grey colors with transparency */
        --ds-color-grey-90: #191C4DE6;
        --ds-color-grey-80: #191C4DCC;
        --ds-color-grey-70: #191C4DB3;
        --ds-color-grey-60: #191C4D99;
        --ds-color-grey-50: #191C4D80;
        --ds-color-grey-40: #191C4D66;
        --ds-color-grey-30: #191C4D4D;
        --ds-color-grey-20: #191C4D33;
        --ds-color-grey-10: #191C4D1A;
        --ds-color-grey-5: #191C4D0D;

        /* Primary colors */
        /* Blue */
        --ds-color-blue-5: #f6fafe;
        --ds-color-blue-10: #edf5fd;
        --ds-color-blue-25: #d1e6fa;
        --ds-color-blue-50: #a3ccf5;
        --ds-color-blue-75: #4a96e3;
        --ds-color-blue-100: #1064ad;
        --ds-color-blue-130: #14477b;
        /* Green */
        --ds-color-green-5: #f4fbfb;
        --ds-color-green-10: #e6f4f4;
        --ds-color-green-25: #cbe7e7;
        --ds-color-green-50: #88c8c8;
        --ds-color-green-75: #429e9e;
        --ds-color-green-100: #037c7c;
        --ds-color-green-130: #0e6262;
        /* Red */
        --ds-color-red-5: #fff6f5;
        --ds-color-red-10: #feedec;
        --ds-color-red-25: #fcd1cf;
        --ds-color-red-50: #f99f9a;
        --ds-color-red-75: #e7615a;
        --ds-color-red-100: #df2920;
        --ds-color-red-130: #c1170b;
        /* Yellow */
        --ds-color-yellow-5: #fffcf0;
        --ds-color-yellow-10: #fef8e1;
        --ds-color-yellow-25: #fdedb4;
        --ds-color-yellow-50: #fce48d;
        --ds-color-yellow-75: #ffe070;
        --ds-color-yellow-100: #ffc800;
        --ds-color-yellow-130: #df9320;
        /* Pink */
        --ds-color-pink-5: #fef1fa;
        --ds-color-pink-10: #fee7f6;
        --ds-color-pink-25: #fccfed;
        --ds-color-pink-50: #f6acdd;
        --ds-color-pink-75: #f499d6;
        --ds-color-pink-100: #f075c7;
        --ds-color-pink-130: #eb47b4;
        /* Purple */
        --ds-color-purple-5: #f4f1fe;
        --ds-color-purple-10: #eae7fe;
        --ds-color-purple-25: #d6cffc;
        --ds-color-purple-50: #b6a8fa;
        --ds-color-purple-75: #a190f9;
        --ds-color-purple-100: #856ef7;
        --ds-color-purple-130: #7055f6;
        /* Orange */
        --ds-color-orange-5: #fff7f0;
        --ds-color-orange-10: #fff0e0;
        --ds-color-orange-25: #ffd9b3;
        --ds-color-orange-50: #ffbf80;
        --ds-color-orange-75: #f59e47;
        --ds-color-orange-100: #f2800d;
        --ds-color-orange-130: #c2660a;

        /* Border radius */
        --ds-border-radius: 4px;

        /* Font */
        --ds-font-family: "Roboto", sans-serif;

        /* Typography */
        --ds-font-weight-body-base: 400;
    }

    *,
    ::before,
    ::after {
        box-sizing: border-box;
    }
`;
/**
 * @deprecated Use `BaseStyles` instead.
 */
exports.GlobalStyle = (0, styled_components_1.createGlobalStyle) `
    /* From this stackoverflow answer, let's only highlight focusable elements rather than all all elements https://stackoverflow.com/questions/1599660/which-html-elements-can-receive-focus/#answer-1600194 */
    a[href],
    area[href],
    input:not([disabled]),
    select:not([disabled]),
    textarea:not([disabled]),
    button:not([disabled]),
    iframe,
    [tabindex],
    [contentEditable="true"]
    {
        &:not([tabindex='-1'])
        {
            &:focus {
                outline: none;
                box-shadow: 0 0 0 4px ${tokens_1.COLOURS.primary.blue[50]};
            }
        }
    }

    /* Chrome default styling targeting button:focus which we need to override: outline: 5px auto -webkit-focus-ring-color; */
    body button:focus {
        outline: none;
    }

    html,
    body {
        color: ${tokens_1.COLOURS.greyscale.night};
        background-color: ${tokens_1.COLOURS.greyscale.cloud};
        font-size: 16px;
        line-height: 1.5;
        margin: 0;

        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
    }

    h1, h2, h3, h4, h5, h6, p, a {
        margin: 0;
    }

    /* Inputs */
    textarea {
        resize: vertical;
    }

    .input-row {
        display: flex;
        flex-wrap: wrap;
        margin-right: -0.375rem;
        margin-left: -0.375rem;
    }

    .input-col-sm-6,
    .input-col-sm-4,
    .input-col-sm-12 {
        width: 100%;
        margin-right: 0.375rem;
        margin-left: 0.375rem;
    }

    @media (min-width: ${tokens_1.BREAKPOINTS.sm}) {
        .input-col-sm-6 {
            width: calc(50% - 0.75rem); /* 2 * 0.375rem */
        }
        .input-col-sm-4 {
            width: calc((100% / 3) - 0.75rem); /* 2 * 0.375rem */
        }
    }

    /* Touch */
    a, area, button, input, label, select, summary, textarea, [tabindex] {
        -ms-touch-action: manipulation;
        touch-action: manipulation;
    }

    /* Accessible hints */
    [aria-busy="true"] {
        cursor: progress;
    }

    [aria-controls] {
        cursor: pointer;
    }

    [aria-disabled="true"], [disabled] {
        cursor: default;
    }
`;
//# sourceMappingURL=GlobalStyle.js.map